import {
  FetchInstrumentation,
  FetchInstrumentationConfig,
} from '@opentelemetry/instrumentation-fetch';
import { FetchCustomAttributeFunction } from '@opentelemetry/instrumentation-fetch/build/src/fetch';
import { UrlMatcher } from '../util/match';
import { coerceArray } from '../util/array';

type AddHeadersFunc = (options: Request | RequestInit, spanUrl: string) => void;

interface HttpSpan {
  attributes: {
    'http.method': string;
    'http.url': string;
  };
}

function isHttpSpan(span: any): span is HttpSpan {
  return span.attributes && span.attributes['http.method'] && span.attributes['http.url'];
}

// exported for testing, but likely not exported from library
export const prettifySpanName: FetchCustomAttributeFunction = (span) => {
  if (isHttpSpan(span)) {
    span.updateName(`${span.attributes['http.method']}[${span.attributes['http.url']}]`);
  }
};

function withDefaults(
  config?: NitroFetchInstrumentationConfig,
): FetchInstrumentationConfig {
  if (!config) {
    config = {};
  }
  if (!config.applyCustomAttributesOnSpan) {
    config.applyCustomAttributesOnSpan = prettifySpanName;
  }
  return config;
}

export interface NitroFetchInstrumentationConfig extends FetchInstrumentationConfig {
  // this is intentionally a different type than propagateTraceHeaderCorsUrls
  propagateTraceHeaderCorsUrlsBlacklist?: UrlMatcher | UrlMatcher[];
}

/**
 * An extension of the standard FetchInstrumentation that provides further capabilities.
 *
 * Some capabilities would be better served existing in the base implementation but are not
 * currently available.
 */
export default class NitroFetchInstrumentation extends FetchInstrumentation {
  private readonly propagateTraceHeaderCorsUrlsBlacklist: UrlMatcher[];
  private readonly superAddHeaders: AddHeadersFunc;

  constructor(config?: NitroFetchInstrumentationConfig) {
    super(withDefaults(config));

    this.propagateTraceHeaderCorsUrlsBlacklist = coerceArray(
      config?.propagateTraceHeaderCorsUrlsBlacklist,
    );

    // override the private _addHeaders method from the parent class
    this.superAddHeaders = (this as any)._addHeaders.bind(this);
    (this as any)._addHeaders = this.blacklistedAddHeaders.bind(this);
  }

  private blacklistedAddHeaders(options: Request | RequestInit, spanUrl: string) {
    if (this.propagateTraceHeaderCorsUrlsBlacklist.find((match) => match(spanUrl))) {
      // this url was blacklisted
      return;
    }

    this.superAddHeaders(options, spanUrl);
  }
}
